import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import { monospaceStyle, panelShadow } from "../../../theme";
import { SupplyFlow, SupplyFlowBand } from "../../../types/analytics";
import { EntityClickHandler } from "../../../types/entityRegistry";
import { AnalyticsSelection, exploreAddress } from "../analyticsState";
import { FlowSelection } from "./SupplyFlowChart";
import {
  BUCKET_WORDS,
  COLUMN_NOUNS,
  bandSelection,
  bandTitle,
  bandsOf,
  columnOfNodeId,
  counted,
  indexNodes,
  needWords,
  nodeLabel,
  nodeSelection,
} from "./supplyFlowModel";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

interface SupplyFlowPanelProps {
  flow: SupplyFlow;
  selected: FlowSelection;
  selection: AnalyticsSelection;
  onSelect: (selection: FlowSelection | null) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected node or band, raised over the top right of the
// canvas: what it is, its counts with the Explore link where a filter
// reproduces them, and the ways on
const SupplyFlowPanel: React.FC<SupplyFlowPanelProps> = ({
  flow,
  selected,
  selection,
  onSelect,
  onOpenEntity,
}) => {
  const nodes = useMemo(() => indexNodes(flow), [flow]);
  const frame = (title: string, subtitle: string, body: React.ReactNode) => (
    <Box
      component="aside"
      aria-label="Selected item"
      // A click in the panel is not a click on the canvas, which clears
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        zIndex: 3,
        width: 300,
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100% - 24px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: "14px",
        overflow: "auto",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        backgroundColor: "background.raised",
        boxShadow: (theme) => panelShadow(theme),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            component="h3"
            sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={monospaceStyle}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Clear selection" onClick={() => onSelect(null)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {body}
    </Box>
  );

  const countField = (
    label: string,
    text: string,
    picked: AnalyticsSelection | null,
  ) => (
    <Field label={label}>
      {picked ? (
        <MuiLink component={Link} to={exploreAddress(picked)} underline="hover">
          {text}
        </MuiLink>
      ) : (
        text
      )}
    </Field>
  );

  const bandButton = (band: SupplyFlowBand, id: string, index: number) => {
    const other = nodes.get(band.source === id ? band.target : band.source);
    return (
      <Box component="li" key={index}>
        <Button
          size="small"
          onClick={() => onSelect({ type: "band", index })}
          sx={{ p: 0, minWidth: 0, textTransform: "none", textAlign: "left" }}
        >
          {other ? nodeLabel(other) : "?"}
        </Button>
        <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
          {counted(band.product_count, band.need ? "host" : "product")}
          {band.need ? " · need" : ""}
          {band.thin ? " · thin" : ""}
        </Typography>
      </Box>
    );
  };

  if (selected.type === "band") {
    const band = flow.bands[selected.index];
    if (!band) {
      return null;
    }
    const picked = bandSelection(selection, band, nodes);
    const hostEnd = [band.source, band.target].some(
      (id) => columnOfNodeId(id) === "hosts",
    );
    return frame(
      bandTitle(band, nodes),
      band.need ? "need band" : "band",
      <>
        {band.need ? (
          <Field label="Hosts">
            {counted(band.product_count, "host")}
            <Typography variant="caption" component="div" color="text.secondary">
              {needWords(band.product_count)}
            </Typography>
          </Field>
        ) : (
          <>
            {countField("Products", counted(band.product_count, "product"), picked)}
            {picked && hostEnd && (
              <Typography variant="caption" color="text.secondary">
                Explore lists the products of the kind; it cannot filter by host.
              </Typography>
            )}
          </>
        )}
        <Field label="Articles">{band.articles}</Field>
        {band.thin && (
          <Typography variant="caption" color="text.secondary">
            Under the support floor: a hint, not a finding.
          </Typography>
        )}
      </>,
    );
  }

  const node = nodes.get(selected.id);
  if (!node) {
    return null;
  }
  const column = columnOfNodeId(node.id);
  const picked = nodeSelection(selection, node);
  const noun = column === "hosts" ? "host" : "product";
  const bands = bandsOf(flow, node.id);
  return frame(
    nodeLabel(node),
    `${column ? COLUMN_NOUNS[column] : "Node"}${node.bucket ? ` · ${BUCKET_WORDS[node.bucket]}` : ""}${node.country ? ` · ${node.country}` : ""}`,
    <>
      {countField(
        column === "hosts" ? "Hosts" : "Products",
        counted(node.product_count, noun),
        picked,
      )}
      {column === "makers" && picked && (
        <Typography variant="caption" color="text.secondary">
          A maker here includes developer, integrator and supplier roles, so the
          count may exceed Explore&apos;s manufacturer filter.
        </Typography>
      )}
      <Field label="Articles">{node.articles}</Field>
      {node.thin && (
        <Typography variant="caption" color="text.secondary">
          Under the support floor: a hint, not a finding.
        </Typography>
      )}
      {bands.length > 0 && (
        <Box component="section" aria-label="Bands of the node">
          <Typography variant="overline" color="text.secondary">
            Bands
          </Typography>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.5 }}>
            {flow.bands.map((band, index) =>
              band.source === node.id || band.target === node.id
                ? bandButton(band, node.id, index)
                : null,
            )}
          </Box>
        </Box>
      )}
      {node.entity && (
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenEntity(node.entity!.id, node.entity!.type)}
            sx={{ textTransform: "none" }}
          >
            Open
          </Button>
        </Box>
      )}
    </>,
  );
};

export default SupplyFlowPanel;
